import { Pipe, PipeTransform, ChangeDetectorRef } from '@angular/core';
import * as moment from 'moment';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface DurationPipeOptions {
    ago?: boolean;
    until?: boolean;
    from?: moment.unitOfTime.DurationConstructor;
    to?: 'default' | 'dh' | 'hm' | moment.unitOfTime.DurationAs;
    now?: number;
}

const DURATION_REFRESH_PERIOD_MS = 30 * 1000;

@Pipe({ name: 'csmsDuration', pure: false })
export class DurationPipe implements PipeTransform {
    onDestroy$ = new Subject<void>();
    intervalSub: Subscription;

    constructor(private cdRef: ChangeDetectorRef) {}

    ngOnDestroy() {
        this.onDestroy$.next();
    }

    transform(value: string | number, options: DurationPipeOptions = {}): any {
        const now = options.now || Date.now();

        if (options.ago) {
            if (!value) {
                return '';
            }

            if (!this.intervalSub) {
                this.intervalSub = interval(DURATION_REFRESH_PERIOD_MS)
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(() => {
                        this.cdRef.markForCheck();
                    });
            }

            return `${moment(value).fromNow()}`;
        }

        const from = options.from || 'milliseconds';
        if (options.until) {
            value = moment(value).diff(moment(now));
        }
        const duration = moment.duration(value, from);
        const d = duration.clone().abs();
        const sign = duration.asMilliseconds() < 0 ? '-' : '';

        const to = options.to || 'default';

        if (to === 'default') {
            if (d.asHours() > 1) {
                return `${sign}${Math.floor(d.asHours())}h ${d.minutes()}m ${d.seconds()}s`;
            } else if (d.asMinutes() > 1) {
                return `${sign}${Math.floor(d.asMinutes())}m ${d.seconds()}s`;
            } else {
                return `${sign}${Math.floor(d.asSeconds())}s`;
            }
        } else if (to === 'dh') {
            if (d.asDays() > 1) {
                return `${sign}${Math.floor(d.asDays())}d ${Math.floor(d.hours())}h`;
            } else if (d.asHours() > 1) {
                return `${sign}${Math.floor(d.asHours())}h ${d.minutes()}m`;
            } else if (d.asMinutes() > 1) {
                return `${sign}${Math.floor(d.asMinutes())}m`;
            } else {
                return `${sign}${Math.floor(d.asSeconds())}s`;
            }
        } else if (to === 'hm') {
            if (d.asHours() > 1) {
                return `${sign}${Math.floor(d.asHours())}h ${d.minutes()}m`;
            } else {
                return `${sign}${Math.floor(d.asMinutes())}m`;
            }
        } else {
            const dur = d.as(to);
            return sign + (dur < 1 ? '<1' : dur.toFixed(0));
        }
    }
}
